import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Download, FileText, AlertTriangle, CheckCircle2, X } from "lucide-react";
import IconAction from "@/components/app/IconAction";
import api from "@/lib_app/api";
import { toast } from "sonner";

export default function ImportProspectModal({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const reset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = (v) => {
    if (busy) return;
    if (!v) reset();
    onOpenChange(v);
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/prospects/import/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_prospek.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Gagal mengunduh template");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/prospects/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.inserted > 0) {
        toast.success(`${data.inserted} prospek berhasil diimpor`);
        onDone?.();
      } else {
        toast.warning("Tidak ada baris yang berhasil diimpor");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal impor");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="import-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#090808] flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#090808]" /> Import Prospek
          </DialogTitle>
          <DialogDescription>
            Unggah file <strong>.xlsx</strong> atau <strong>.csv</strong>. Kolom minimum:
            {" "}<code className="text-[11px] bg-[#F4F5F7] px-1 py-0.5 rounded">customer</code>,
            {" "}<code className="text-[11px] bg-[#F4F5F7] px-1 py-0.5 rounded">service</code>,
            {" "}<code className="text-[11px] bg-[#F4F5F7] px-1 py-0.5 rounded">rate</code> (1/2/3).
            Kolom opsional: <code className="text-[11px] bg-[#F4F5F7] px-1 py-0.5 rounded">sales</code> (email atau nama) &{" "}
            <code className="text-[11px] bg-[#F4F5F7] px-1 py-0.5 rounded">bulan</code> (YYYY-MM) untuk backdate.
          </DialogDescription>
        </DialogHeader>

        <div className="pt-2 space-y-4">
          <button
            type="button"
            onClick={downloadTemplate}
            data-testid="download-template-btn"
            className="w-full flex items-center gap-3 rounded-xl border border-[#DCE0E7] bg-[#F4F5F7] hover:bg-[#F4F5F7] transition-colors px-4 py-3 text-left"
          >
            <div className="w-9 h-9 rounded-lg bg-[#DCE0E7] text-[#090808] flex items-center justify-center">
              <Download className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[#090808]">Unduh template</div>
              <div className="text-xs text-[#A0A3A8]">Sudah berisi header + 1 baris contoh</div>
            </div>
          </button>

          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold mb-1.5">Pilih file</div>
              <div className="rounded-xl border-2 border-dashed border-[#DCE0E7] bg-white hover:border-[#090808]/40 transition-colors px-4 py-6 text-center cursor-pointer">
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xlsm,.csv"
                  onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                  data-testid="import-file-input"
                  className="hidden"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-[#090808]" />
                    <span className="font-semibold text-[#090808] truncate">{file.name}</span>
                    <span className="text-[11px] text-[#A0A3A8]">({Math.round(file.size / 1024)} KB)</span>
                  </div>
                ) : (
                  <div className="text-sm text-[#A0A3A8]" onClick={() => inputRef.current?.click()}>
                    Klik untuk pilih atau seret file ke sini
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="mt-2 text-xs text-[#090808] hover:underline font-semibold"
              >
                {file ? "Ganti file" : "Pilih file"}
              </button>
            </label>

            {result && (
              <div className="rounded-xl bg-[#F4F5F7] p-3 space-y-2" data-testid="import-result">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#57D28A]" />
                  <span className="font-semibold text-[#090808]">{result.inserted} berhasil</span>
                  <span className="text-[#A0A3A8]">dari {result.total_rows} baris</span>
                </div>
                {(result.errors || []).length > 0 && (
                  <div className="space-y-1 pt-1 border-t border-[#DCE0E7]">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#7A6633]">
                      <AlertTriangle className="w-3.5 h-3.5" /> {result.errors.length} baris ditolak
                    </div>
                    <ul className="text-[11px] text-[#626061] max-h-28 overflow-y-auto space-y-0.5">
                      {result.errors.slice(0, 10).map((e, i) => (
                        <li key={i}>Baris {e.row}: {e.reason}</li>
                      ))}
                      {result.errors.length > 10 && <li className="text-[#A0A3A8]">…dan {result.errors.length - 10} lainnya</li>}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <IconAction icon={X} label={result ? "Tutup" : "Batal"} onClick={() => handleClose(false)} testid="import-cancel-btn" />
              <IconAction icon={Upload} label={busy ? "Mengimpor..." : "Impor"} variant="primary" type="submit" disabled={!file || busy} testid="import-submit-btn" />
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
